import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";

interface TooltipState {
  text: string;
  x: number;
  y: number;
  visible: boolean;
}

/**
 * Helper to check if an element's text is visually truncated.
 * Synchronously lifts CSS constraints to measure the true unconstrained width.
 */
const isElementTruncated = (el: HTMLElement): boolean => {
  if (el.scrollWidth > el.clientWidth + 1) {
    return true;
  }

  // Save original inline styles
  const origOverflow = el.style.overflow;
  const origTextOverflow = el.style.textOverflow;
  const origWhiteSpace = el.style.whiteSpace;
  const origWidth = el.style.width;
  const origMaxWidth = el.style.maxWidth;

  // Temporarily expand to measure natural width
  el.style.overflow = "visible";
  el.style.textOverflow = "clip";
  el.style.whiteSpace = "nowrap";
  el.style.width = "auto";
  el.style.maxWidth = "none";

  const naturalWidth = el.scrollWidth;

  // Restore original styles
  el.style.overflow = origOverflow;
  el.style.textOverflow = origTextOverflow;
  el.style.whiteSpace = origWhiteSpace;
  el.style.width = origWidth;
  el.style.maxWidth = origMaxWidth;

  return naturalWidth > el.clientWidth + 1;
};

/**
 * GlobalTooltipPortal
 * Mount this ONCE at the top of your app (e.g. in App.tsx or main layout).
 * It listens for mouseenter/mouseleave on ANY element that has a
 * `data-tooltip="…"` attribute and renders an instant tooltip via a portal
 * onto document.body — so it is NEVER clipped by overflow:hidden parents.
 */
const GlobalTooltipPortal: React.FC = () => {
  const [tooltip, setTooltip] = useState<TooltipState>({
    text: "",
    x: 0,
    y: 0,
    visible: false,
  });

  const handleMouseEnter = useCallback((e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const el = target.closest("[data-tooltip]") as HTMLElement | null;
    if (!el) return;
    const text = el.getAttribute("data-tooltip");
    if (!text) return;

    // Check if the element contains text and if it's a value tooltip.
    // A value tooltip matches or contains the visible text content of the element.
    const cleanTooltip = text.trim().toLowerCase();
    const cleanText = el.textContent?.trim().toLowerCase() || "";

    const isValueTooltip = cleanText && (
      cleanTooltip === cleanText ||
      cleanTooltip.includes(cleanText) ||
      cleanText.includes(cleanTooltip)
    );

    const isTruncated = isValueTooltip ? isElementTruncated(el) : true;

    if (isValueTooltip && !isTruncated) {
      return;
    }

    const rect = el.getBoundingClientRect();
    setTooltip({
      text,
      x: rect.left + rect.width / 2,
      y: rect.top,
      visible: true,
    });
  }, []);

  const handleMouseLeave = useCallback((e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const el = target.closest("[data-tooltip]");
    if (!el) return;
    setTooltip((prev) => ({ ...prev, visible: false }));
  }, []);

  const handleScroll = useCallback(() => {
    setTooltip((prev) => ({ ...prev, visible: false }));
  }, []);

  useEffect(() => {
    document.addEventListener("mouseover", handleMouseEnter, true);
    document.addEventListener("mouseout", handleMouseLeave, true);
    document.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mouseover", handleMouseEnter, true);
      document.removeEventListener("mouseout", handleMouseLeave, true);
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, [handleMouseEnter, handleMouseLeave, handleScroll]);

  if (!tooltip.visible || !tooltip.text) return null;

  const GAP = 8;
  const ARROW = 6;
  const style: React.CSSProperties = {
    position: "fixed",
    top: tooltip.y - GAP - ARROW,
    left: tooltip.x,
    transform: "translateX(-50%) translateY(-100%)",
    zIndex: 999999,
    pointerEvents: "none",
    maxWidth: 300,
    background: "#131327",
    color: "#fff",
    fontSize: 11,
    fontWeight: 600,
    lineHeight: 1.45,
    padding: "5px 10px",
    borderRadius: 7,
    boxShadow: "0 4px 16px rgba(0,0,0,0.22)",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    textAlign: "center",
  };

  return createPortal(
    <div style={style}>
      {tooltip.text}
      {/* Arrow */}
      <span
        style={{
          position: "absolute",
          bottom: -ARROW + 1,
          left: "50%",
          transform: "translateX(-50%)",
          width: 0,
          height: 0,
          borderLeft: `${ARROW}px solid transparent`,
          borderRight: `${ARROW}px solid transparent`,
          borderTop: `${ARROW}px solid #131327`,
        }}
      />
    </div>,
    document.body
  );
};

export default GlobalTooltipPortal;
